import { CustomAccessory, CustomUnit, PerformanceAccessoryAnswer } from '@mitsubishi/shared/data-access';

import { Probability } from '../../enums';
import { MoveChildRequest, Selection, SelectionDetails, StatisticDataArray } from '../../models';
import { UpdateOrderFieldsRequest } from '../../models/requests/update-order-fields.model';

abstract class BaseSelectionAction {
  constructor(public selectionId: string) {}
}
export namespace SelectionActions {
  export class Create {
    static readonly type = '[Selection] Create';
    constructor(
      public projectId: string,
      public organizationId: number
    ) {}
  }

  export class Open extends BaseSelectionAction {
    static readonly type = '[Selection] Open/Navigate';
  }

  export class Add {
    static readonly type = '[Selection] Add';
    constructor(public selection: Selection) {}
  }

  export class Update {
    static readonly type = '[Selection] Update';
    constructor(
      public selectionId: string,
      public title: string
    ) {}
  }

  export class UpdateStateDetails {
    static readonly type = '[Selection] Update state details';
    constructor(public details: Partial<SelectionDetails>) {}
  }

  export class Duplicate extends BaseSelectionAction {
    static readonly type = '[Selection] Duplicate';
  }

  export class Consolidate {
    static readonly type = '[Selection] Consolidate';
    constructor(
      public projectId: string,
      public selectionId: string
    ) {}
  }

  export class DraftQuote {
    static readonly type = '[Selection] Draft';
    constructor(
      public projectId: string,
      public selectionId: string
    ) {}
  }

  export class ConfirmAlternative {
    static readonly type = '[Selection] Confirm alternative';
    constructor(
      public projectId: string,
      public selectionId: string,
      public alternativeId: string
    ) {}
  }

  export class Delete extends BaseSelectionAction {
    static readonly type = '[Selection] Delete';
  }

  export class UpdateCurrencyMultiplier {
    static readonly type = '[Selection] Update currency multiplier';
    constructor(public currencyMultiplier: number | null) {}
  }

  export class UpdateGlobalPriceMultiplier {
    static readonly type = '[Selection] Update global price multiplier';
  }

  export class UpdatePriceMultiplier {
    static readonly type = '[Selection] Update price multiplier';
    constructor(public priceMultiplier: number) {}
  }

  export class UpdateCustomCurrency {
    static readonly type = '[Selection] Update custom currency';
    constructor(public customCurrency: string | null) {}
  }

  export class ToggleUseCustomCurrency {
    static readonly type = '[Selection] Toggle use custom currency';
  }

  export class ToggleIsHot {
    static readonly type = '[Selection] Toggle is hot';
  }
  /** @deprecated Use {@link PatchSelection} instead */
  export class EditNote {
    static readonly type = '[Selection] Edit Note';
    constructor(
      public projectId: string,
      public selectionId: string,
      public note: string
    ) {}
  }

  export class PatchSelection {
    static readonly type = '[Selection] Patch';
    constructor(public patch: Partial<{ title: string; notes: string }>) {}
  }

  export class UpdateCurrencyCode {
    static readonly type = '[Selection] Update Currency Code';
    constructor(
      public currencyCode: string,
      public requestTraceId?: string
    ) {}
  }

  export class EditAlternativeDescription {
    static readonly type = '[Selection] Edit Alternative Description';
    constructor(
      public projectId: string,
      public selectionId: string,
      public alternativeId: string,
      public note: string
    ) {}
  }

  export class ShareOffer {
    static readonly type = '[Selection] Share Offer';
    constructor(
      public email: string[],
      public priceVisibility: string,
      public priceType: string,
      public noteVisibility: string,
      public datasheetVisibility: string,
      public documentVisibility: string
    ) {}
  }

  export class OpenShared {
    static readonly type = '[Selection] Navigate Share Offer';
    constructor(
      public params: {
        priceVisibility: string;
        priceType: string;
        noteVisibility: string;
        datasheetVisibility: string;
        documentVisibility: string;
      }
    ) {}
  }

  export class ExportOfferXls {
    static readonly type = '[Selection] Export Offer XLS';
    constructor(
      public fileName: string,
      public priceType?: string
    ) {}
  }

  export class UpdateOfferRecipient {
    static readonly type = '[Selection] Update Offer Recipient';
    constructor(
      public selectionId: string,
      public offerRecipient: string
    ) {}
  }

  export class UpdateAddress {
    static readonly type = '[Selection] Update Address';
    constructor(
      public selectionId: string,
      public recipientAddress: string
    ) {}
  }

  export class UpdateOrderFields {
    static readonly type = '[Selection] Update Order Fields';
    constructor(
      public selectionId: string,
      public orderFields: UpdateOrderFieldsRequest
    ) {}
  }

  export class AddAdditionalInfo {
    static readonly type = '[Selection] Add Additional Info';
    constructor(
      public selectionId: string,
      public title: string,
      public description: string,
      public inEvidence: boolean,
      public categoryId: string,
      public templateId: string
    ) {}
  }

  export class UpdateAdditionalInfo {
    static readonly type = '[Selection] Update Additional Info';
    constructor(
      public selectionId: string,
      public additionalInfoId: string,
      public title: string,
      public description: string,
      public inEvidence: boolean,
      public categoryId: string,
      public templateId: string
    ) {}
  }

  export class DeleteAdditionalInfo {
    static readonly type = '[Selection] Delete Additional Info';
    constructor(
      public selectionId: string,
      public additionalInfoId: string
    ) {}
  }

  export class FinalizeVersion {
    static readonly type = '[Selection] Finalize Version';
    constructor(public selectionId: string) {}
  }

  export class CreateRevision {
    static readonly type = '[Selection] Create Revision';
    constructor(public selectionId: string) {}
  }
  export class SetStatisticData {
    static readonly type = '[Selection] Set Statistic Data';
    constructor(
      public selectionId: string,
      public statisticData: StatisticDataArray[]
    ) {}
  }

  export class UpdatePriceValidityDate {
    static readonly type = '[Selection] Update Price Validity Date';
    constructor(
      public selectionId: string,
      public priceValidityDate: string | null
    ) {}
  }

  export class ToggleTestPriceList {
    static readonly type = '[Selection] Toggle Test Price List';
    constructor(public selectionId: string) {}
  }

  export class UpdateExpiringDate {
    static readonly type = '[Selection] Update Expiring Date';
    constructor(
      public selectionId: string,
      public expiringDate: string | null
    ) {}
  }

  export class UpdateOfferProbability {
    static readonly type = '[Selection]  Update offer probability';
    constructor(public probability: Probability) {}
  }

  export class SetOfferLost {
    static readonly type = '[Selection] Set Offer Lost';
  }

  export class UnitSoftDelete {
    static readonly type = '[Unit] Soft delete';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class UnitRestore {
    static readonly type = '[Unit] Restore';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class UnitDelete {
    static readonly type = '[Unit] Delete';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class CreateUnitFromTemplate {
    static readonly type = '[Unit] Create From Template';
    constructor(
      public selectionId: string,
      public alternativeId: string,
      public entityId: string,
      public entityType: string
    ) {}
  }

  export class CreateUnitFromBleen {
    static readonly type = '[Unit] Create From Bleen Entity';
    constructor(
      public selectionId: string,
      public alternativeId: string,
      public bleenId: string
    ) {}
  }

  export class CreateUnitAndConfigFromBleen {
    static readonly type = '[Unit] Create and config From Bleen Entity';
    constructor(
      public selectionId: string,
      public alternativeId: string,
      public bleenId: string
    ) {}
  }

  export class UnitDuplicate {
    static readonly type = '[Unit] Duplicate';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class UnitMove {
    static readonly type = '[Unit] Move';
    constructor(
      public unitId: string,
      public position: number
    ) {}
  }

  export class MoveUnitCustom {
    static readonly type = '[Unit Custom] Move';
    constructor(
      public unitId: string,
      public position: number
    ) {}
  }

  export class UpdateUnitQuantity {
    static readonly type = '[Unit] Update Unit Quantity';
    constructor(
      public unitId: string,
      public alternativeId: string,
      public quantity: number
    ) {}
  }

  export class UpdateUnitCustomQuantity {
    static readonly type = '[Unit] Update UnitCustom Quantity';
    constructor(
      public unitId: string,
      public alternativeId: string,
      public quantity: number
    ) {}
  }

  export class AlternativeCreate {
    static readonly type = '[Alternative] Create';
    constructor(public selectionId: string) {}
  }

  export class AlternativeDelete {
    static readonly type = '[Alternative] Delete';
    constructor(
      public selectionId: string,
      public alternativeId: string
    ) {}
  }

  export class AlternativePricelist {
    static readonly type = '[Alternative] Pricelist alternative';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class AlternativeUpdatePriceForCustomer {
    static readonly type = '[Alternative] Update price for customer';
    constructor(
      public alternativeId: string,
      public priceForCustomer: number | null
    ) {}
  }

  export class AlternativeMoveRow {
    static readonly type = '[Alternative] Move row';
    constructor(
      public alternativeId: string,
      public moveChildRequest: MoveChildRequest
    ) {}
  }

  export class UnitAlternativeLink {
    static readonly type = '[UnitAlternative] Link unit alternative';
    constructor(
      public selectionId: string,
      public unitId: string,
      public alternativeId: string
    ) {}
  }

  export class UnitAlternativeUnlink {
    static readonly type = '[UnitAlternative] Unlink unit alternative';
    constructor(
      public selectionId: string,
      public unitId: string,
      public alternativeId: string
    ) {}
  }

  export class UnitCustomAlternativeLink {
    static readonly type = '[UnitAlternative] Link unit custom alternative';
    constructor(
      public selectionId: string,
      public unitId: string,
      public alternativeId: string
    ) {}
  }

  export class UnitCustomAlternativeUnlink {
    static readonly type = '[UnitAlternative] Unlink unit custom alternative';
    constructor(
      public selectionId: string,
      public unitId: string,
      public alternativeId: string
    ) {}
  }

  export class AlternativeExtraCreate {
    static readonly type = '[AlternativeExtra] Create';
    constructor(public alternativeId: string) {}
  }

  export class AlternativeExtraDelete {
    static readonly type = '[AlternativeExtra] Deltete';
    constructor(public alternativeExtraId: string) {}
  }

  export class AlternativeExtraUpdatePrice {
    static readonly type = '[AlternativeExtra] Update Cost';
    constructor(
      public alternativeExtraId: string,
      public price: number
    ) {}
  }

  export class AlternativeExtraUpdateCustomerPrice {
    static readonly type = '[AlternativeExtra] Update Selling';
    constructor(
      public alternativeExtraId: string,
      public customer: number
    ) {}
  }

  export class AlternativeExtraUpdateName {
    static readonly type = '[AlternativeExtra] Update Name';
    constructor(
      public alternativeExtraId: string,
      public name: string
    ) {}
  }

  export class DatasheetCreatePdf {
    static readonly type = '[UnitDatasheet] Create Pdf';
    constructor(
      public unitId: string,
      public requestTraceId?: string
    ) {}
  }

  export class DatasheetDownloadPdf {
    static readonly type = '[UnitDatasheet] download Pdf';
    constructor(
      public fileName: string,
      public customFileName?: string
    ) {}
  }

  export class ConfirmOrder {
    static readonly type = '[Selection] Confirm order';
    constructor(public selectionId: string) {}
  }

  export class UpdateAccessoryQuantity {
    static readonly type = '[Accessory] Update accessory quantity';
    constructor(
      public accessoryId: string,
      public quantity: number
    ) {}
  }

  export class GoToUnitConfiguration {
    static readonly type = '[Unit] Go to unit configuration';
    constructor(public unitId: string) {}
  }

  export class CustomUnitCreate {
    static readonly type = '[Unit Custom] Create';
    constructor(public unitCustom: CustomUnit) {}
  }

  export class CustomUnitCreateSuccess {
    static readonly type = '[Unit Custom] Create Success';
    constructor(
      public customUnitPayload: CustomUnit,
      public createdUnitId: string
    ) {}
  }

  export class CustomUnitEdit {
    static readonly type = '[Unit Custom] Edit';
    constructor(
      public unitId: string,
      public unitCustom: CustomUnit
    ) {}
  }

  export class UnitCustomSoftDelete {
    static readonly type = '[Unit Custom] Soft delete';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class UnitCustomDelete {
    static readonly type = '[Unit Custom] Delete';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class UnitCustomRestore {
    static readonly type = '[Unit Custom] Restore';
    constructor(
      public selectionId: string,
      public unitId: string
    ) {}
  }

  export class CreateAccessoryCustom {
    static readonly type = '[Accessory Custom] Create accessory';
    constructor(
      public unitId: string,
      public accessoryCustom: CustomAccessory,
      public isCustomUnit: boolean
    ) {}
  }

  export class CustomAccessoryCreateSuccess {
    static readonly type = '[Accessory Custom] Create Success';
    constructor(
      public unitId: string,
      public isCustomUnit: boolean,
      public createdAccessoryId: string
    ) {}
  }

  export class UpdateAccessoryCustom {
    static readonly type = '[Accessory] Update accessory custom quantity';
    constructor(
      public unitId: string,
      public acessory: CustomAccessory,
      public isCustomUnit: boolean
    ) {}
  }
  export class DeleteAccessoryCustom {
    static readonly type = '[Accessory] Delete accessory custom';
    constructor(
      public unitId: string,
      public acessoryId: string,
      public isCustomUnit: boolean
    ) {}
  }

  export class UpdateUnitWarranty {
    static readonly type = '[Unit] Update unit warranty';
    constructor(
      public unitId: string,
      public warrantyCode: string,
      public alternativeId: string
    ) {}
  }

  export class DownloadWorkingConditionsRequestLog {
    static readonly type = '[Unit] Download working conditions request log';
  }

  export class DownloadPerformanceRequestLog {
    static readonly type = '[Unit] Download performance request log';
  }

  export class UpdateAccessoryPrice {
    static readonly type = '[Accessory] Update accessory price';
    constructor(
      public accessoryId: string,
      public price: number
    ) {}
  }

  export class AssignAccessories {
    static readonly type = '[Unit] Assign accessories';
    constructor(public answers: PerformanceAccessoryAnswer[]) {}
  }

  export class UpdateCustomUnitWarranty {
    static readonly type = '[Unit Custom] Update unit warranty';
    constructor(
      public unitId: string,
      public warrantyCode: string,
      public alternativeId: string
    ) {}
  }

  export class DownloadOrderLog {
    static readonly type = '[Unit] Download order log';
  }

  export class DownloadOfferPDF {
    static readonly type = '[Unit] Download offer PDF';
    constructor(
      public priceVisibility: string,
      public priceType: string,
      public noteVisibility: string,
      public datasheetVisibility: string,
      public documentVisibility: string
    ) {}
  }

  export class DownloadOrderPDF {
    static readonly type = '[Unit] Download order PDF';
    constructor(public productionSiteCode: string) {}
  }

  export class AddExternalUnit {
    static readonly type = '[Unit] Add external unit';
    constructor(
      public bleenId: string,
      public quantity: number,
      public workingConditions: any,
      public requestTraceId?: string
    ) {}
  }

  export class UpgradeModels {
    static readonly type = '[Unit] Upgrade models';
  }

  export class CheckModelsHasUpgrade {
    static readonly type = '[Selection] Check models has upgrade';
  }

  export class RefreshPrice {
    static readonly type = '[Selection] Refresh price';
  }

  export class UpdateUnitDescription {
    static readonly type = '[Unit] Update unit description';
    constructor(
      public unitId: string,
      public description?: string
    ) {}
  }

  export class UpdateUnitCustomDescription {
    static readonly type = '[Unit Custom] Update unit custom description';
    constructor(
      public unitCustomId: string,
      public description?: string
    ) {}
  }

  export class DeleteAlternativeUnits {
    static readonly type = '[Selection] Delete alternative units';
    constructor(
      public alternativeId: string,
      public unitIds: string[]
    ) {}
  }
}
